/** /claim?session=<id> — a guest saves their history: email and password turn the guest into a
 * student in place (same identity), then they are signed in and taken to their mastery. */
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { serviceUrl } from "../api/base";
import { useSession } from "../auth/SessionContext";
import { keepTokens } from "../auth/session";
import { recallJoin } from "../live/studentSession";
import { product } from "../product";
import { Button, PasswordInput } from "../ui";

export function Claim() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reload } = useSession();
  const { session } = useSearch({ strict: false }) as { session?: string };
  const joined = session ? recallJoin(session) : null;
  const guestToken = joined?.guest_token ?? null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!guestToken) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(serviceUrl("/auth/v1/guests/promote"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${guestToken}` },
        body: JSON.stringify({ email, password, display_name: name || null }),
      });
      if (!res.ok) {
        setError(res.status === 409 ? t("claim.taken") : t("claim.failed"));
        return;
      }
      const pair = (await res.json()) as { access_token: string; refresh_token: string | null };
      keepTokens(pair.access_token, pair.refresh_token, true);
      await reload();
      void navigate({ to: "/me" });
    } catch {
      setError(t("claim.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="board font-system flex min-h-dvh flex-col justify-center gap-6 p-6">
      <header className="mx-auto w-full max-w-md">
        <h1 className="text-2xl font-bold tracking-tight" translate="no">{product.name}</h1>
        <p className="mt-1 text-lg">{t("claim.title")}</p>
        <p className="mt-1 text-sm text-[color:var(--ink-2)]">{t("claim.lede")}</p>
      </header>
      {!guestToken ? (
        <p className="mx-auto w-full max-w-md rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm">{t("claim.noGuest")}</p>
      ) : (
        <form
          className="mx-auto flex w-full max-w-md flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">{t("claim.email")}</span>
            <input className="field" type="email" name="email" inputMode="email" autoComplete="email" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <PasswordInput label={t("claim.password")} name="password" autoComplete="new-password" minLength={8} value={password} onChange={setPassword} required />
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">{t("claim.name")}</span>
            <input className="field" name="name" autoComplete="name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          {error && <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>{error}</p>}
          <Button type="submit" block loading={busy}>{t("claim.cta")}</Button>
          <p className="text-xs text-[color:var(--ink-2)]">{t("claim.privacy")}</p>
        </form>
      )}
    </main>
  );
}
